<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" v-if="isOpen">
    <div class="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
      <div class="p-6">
        <div class="flex justify-between items-center mb-6">
          <h3 class="text-xl font-semibold text-slate-800">Указать продукцию для комплектования</h3>
          <button @click="closeModal" class="text-slate-400 hover:text-slate-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        <div v-if="loading" class="text-center py-8">
          <div class="text-slate-600">Загрузка...</div>
        </div>
        
        <div v-else class="space-y-4">
          <!-- Продукция -->
          <div>
            <label class="block text-sm font-medium text-slate-700 mb-1">Продукция:</label>
            <v-select
              v-model="selectedProduct"
              @input="onFilterChange"
              :options="productOptions"
              placeholder="Выберите продукцию"
              label="label"
              :reduce="option => option.value"
              class="w-full"
            />
          </div>

          <!-- Серийный номер -->
          <div>
            <label class="block text-sm font-medium text-slate-700 mb-1">Серийный номер:</label>
            <v-select
              v-model="selectedSerialNumber"
              @input="onFilterChange"
              :options="serialNumberOptions"
              :disabled="!selectedProduct"
              placeholder="Выберите серийный номер"
              class="w-full"
            />
          </div>

          <!-- Поставщик -->
          <div>
            <label class="block text-sm font-medium text-slate-500 mb-1">Поставщик:</label>
            <v-select
              v-model="selectedSupplier"
              @input="onFilterChange"
              :options="supplierOptions"
              :disabled="!selectedProduct"
              placeholder="Необязательно"
              class="w-full"
            />
          </div>

          <!-- Партия -->
          <div>
            <label class="block text-sm font-medium text-slate-500 mb-1">Партия:</label>
            <v-select
              v-model="selectedBatch"
              @input="onFilterChange"
              :options="batchOptions"
              :disabled="!selectedProduct"
              placeholder="Необязательно"
              label="label"
              :reduce="option => option.value"
              class="w-full"
            />
          </div>

          <!-- Ячейка -->
          <div>
            <label class="block text-sm font-medium text-slate-500 mb-1">Ячейка:</label>
            <v-select
              v-model="selectedCell"
              @input="onFilterChange"
              :options="cellOptions"
              :disabled="!selectedProduct"
              placeholder="Необязательно"
              class="w-full"
            />
          </div>
        </div>
        
        <div class="flex justify-end space-x-3 mt-6 pt-4 border-t border-slate-200">
          <button @click="closeModal" class="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium">
            Отмена
          </button>
          <button @click="saveSelection" :disabled="!canSave" class="px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed">
            Сохранить
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import vSelect from 'vue-select'

export default {
  name: 'ComponentPlanningModal',
  components: {
    'v-select': vSelect
  },
  data() {
    return {
      isOpen: false,
      operationStepId: null,
      productionTaskId: null,
      
      // Исходные данные с API
      allStorehouseProducts: [],
      
      // Выбранные значения
      selectedProduct: null,
      selectedBatch: null,
      selectedSupplier: null,
      selectedSerialNumber: null,
      selectedCell: null,
      
      // Опции для селектов (вычисляются)
      productOptions: [],
      batchOptions: [],
      supplierOptions: [], 
      serialNumberOptions: [],
      cellOptions: [],
      
      loading: false
    }
  },
  computed: {
    canSave() {
      // Обязательны только продукция и серийный номер
      return this.selectedProduct && this.selectedSerialNumber;
    }
  },
  beforeUnmount() {
    // Разблокируем скролл при уничтожении компонента
    this.unlockBodyScroll();
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.lockBodyScroll();
      } else {
        this.unlockBodyScroll();
      }
    },
    selectedProduct() {
      // console.log('selectedProduct changed:', this.selectedProduct);
      // При смене продукции сбрасываем все остальные селекты
      this.selectedBatch = null;
      this.selectedSupplier = null;
      this.selectedSerialNumber = null;
      this.selectedCell = null;
      this.updateAllOptions();
    },
    selectedBatch() {
      // console.log('selectedBatch changed:', this.selectedBatch);
      this.updateAllOptions();
    },
    selectedSupplier() {
      // console.log('selectedSupplier changed:', this.selectedSupplier);
      this.updateAllOptions();
    },
    selectedSerialNumber() {
      // console.log('selectedSerialNumber changed:', this.selectedSerialNumber);
      this.updateAllOptions();
    },
    selectedCell() {
      console.log('selectedCell changed:', this.selectedCell);
      this.updateAllOptions();
    }
  },
  methods: {
    // При открытии модалки
    async openModal(operationStepId, productionTaskId, allowedProductIds = null) {
      this.isOpen = true;
      this.operationStepId = operationStepId;
      this.productionTaskId = productionTaskId;
      this.allowedProductIds = allowedProductIds;
      this.resetForm();

      this.loading = true;
      try {
        const response = await axios.post('/api/component-planning/get-products', {
          operation_step_id: operationStepId
        });
        let products = response.data.data;
        // Фильтруем по разрешённым product_id (лимит не достигнут)
        if (this.allowedProductIds) {
          products = products.filter(p => this.allowedProductIds.includes(p.product_id));
        }
        this.allStorehouseProducts = products;
        // console.log('Полученные продукты с API:', this.allStorehouseProducts);
        this.updateAllOptions();
        // console.log('Сформированные опции:', {
        //   products: this.productOptions,
        //   batches: this.batchOptions,
        //   suppliers: this.supplierOptions,
        //   serials: this.serialNumberOptions,
        //   cells: this.cellOptions
        // });
      } catch (error) {
        console.error('Ошибка загрузки продукции:', error);
        alert('Ошибка загрузки данных');
      } finally {
        this.loading = false;
      }
    },
    
    closeModal() {
      this.isOpen = false;
      this.unlockBodyScroll();
      this.resetForm();
    },
    
    resetForm() {
      this.selectedProduct = null;
      this.selectedBatch = null;
      this.selectedSupplier = null;
      this.selectedSerialNumber = null;
      this.selectedCell = null;
      this.allStorehouseProducts = [];
      this.updateAllOptions();
    },
    
    // Обновление всех опций на основе выбранных фильтров
    updateAllOptions() {
      // console.log('updateAllOptions начал работу с данными:', this.allStorehouseProducts.length, 'продуктов');
      
      // Продукция - всегда все опции (главный селект)
      const productMap = new Map();
      this.allStorehouseProducts.forEach(p => {
        if (p.product?.name && p.product_id) {
          productMap.set(p.product_id, {
            value: p.product_id,
            label: p.product.name
          });
        }
      });
      this.productOptions = Array.from(productMap.values());
      
      // Если продукция не выбрана - остальные селекты пустые
      if (!this.selectedProduct) {
        this.batchOptions = [];
        this.supplierOptions = [];
        this.serialNumberOptions = [];
        this.cellOptions = [];
        // console.log('Продукция не выбрана, остальные селекты очищены');
        return;
      }
      
      // Базовая фильтрация - только по выбранной продукции
      const baseFilteredProducts = this.allStorehouseProducts.filter(p => p.product_id === this.selectedProduct);
      
      // console.log('Продуктов для выбранной продукции:', baseFilteredProducts.length);
      
      // Для каждого селекта формируем опции БЕЗ учета его собственного фильтра
      
      // Партии - фильтруем по всем кроме партии
      let productsForBatch = baseFilteredProducts;
      if (this.selectedSupplier) {
        productsForBatch = productsForBatch.filter(p => p.batch_product?.batch_manufacture?.supplier?.name === this.selectedSupplier);
      }
      if (this.selectedSerialNumber) {
        productsForBatch = productsForBatch.filter(p => p.batch_product?.serial_number === this.selectedSerialNumber);
      }
      if (this.selectedCell) {
        productsForBatch = productsForBatch.filter(p => this.getCellAddress(p) === this.selectedCell);
      }
      // Партии с датой изготовления
      const batchMap = new Map();
      productsForBatch.forEach(p => {
        const batchManufacture = p.batch_product?.batch_manufacture;
        if (batchManufacture?.number) {
          const key = batchManufacture.number;
          if (!batchMap.has(key)) {
            const date = batchManufacture.manufacture_date 
              ? new Date(batchManufacture.manufacture_date).toLocaleDateString('ru-RU')
              : 'N/A';
            batchMap.set(key, {
              value: batchManufacture.number,
              label: `${batchManufacture.number} (от ${date})`
            });
          }
        }
      });
      this.batchOptions = Array.from(batchMap.values());
      
      // Производители - фильтруем по всем кроме производителя
      let productsForSupplier = baseFilteredProducts;
      if (this.selectedBatch) {
        productsForSupplier = productsForSupplier.filter(p => p.batch_product?.batch_manufacture?.number === this.selectedBatch);
      }
      if (this.selectedSerialNumber) {
        productsForSupplier = productsForSupplier.filter(p => p.batch_product?.serial_number === this.selectedSerialNumber);
      }
      if (this.selectedCell) {
        productsForSupplier = productsForSupplier.filter(p => this.getCellAddress(p) === this.selectedCell);
      }
      this.supplierOptions = [...new Set(productsForSupplier.map(p => p.batch_product?.batch_manufacture?.supplier?.name).filter(Boolean))];
      
      // Серийные номера - фильтруем по всем кроме серийного номера
      let productsForSerial = baseFilteredProducts;
      if (this.selectedBatch) {
        productsForSerial = productsForSerial.filter(p => p.batch_product?.batch_manufacture?.number === this.selectedBatch);
      }
      if (this.selectedSupplier) {
        productsForSerial = productsForSerial.filter(p => p.batch_product?.batch_manufacture?.supplier?.name === this.selectedSupplier);
      }
      if (this.selectedCell) {
        productsForSerial = productsForSerial.filter(p => this.getCellAddress(p) === this.selectedCell);
      }
      this.serialNumberOptions = [...new Set(productsForSerial.map(p => p.batch_product?.serial_number).filter(Boolean))];
      
      // Ячейки - фильтруем по всем кроме ячейки
      let productsForCell = baseFilteredProducts;
      if (this.selectedBatch) {
        productsForCell = productsForCell.filter(p => p.batch_product?.batch_manufacture?.number === this.selectedBatch);
      }
      if (this.selectedSupplier) {
        productsForCell = productsForCell.filter(p => p.batch_product?.batch_manufacture?.supplier?.name === this.selectedSupplier);
      }
      if (this.selectedSerialNumber) {
        productsForCell = productsForCell.filter(p => p.batch_product?.serial_number === this.selectedSerialNumber);
      }
      this.cellOptions = [...new Set(productsForCell.map(p => this.getCellAddress(p)).filter(Boolean))];
      
      // console.log('updateAllOptions завершен, новые опции:', {
      //   productOptions: this.productOptions.length,
      //   batchOptions: this.batchOptions.length,
      //   supplierOptions: this.supplierOptions.length,
      //   serialNumberOptions: this.serialNumberOptions.length,
      //   cellOptions: this.cellOptions.length
      // });
    },
    
    // При изменении любого селекта
    onFilterChange() {
      // console.log('onFilterChange вызван, текущие значения:', {
      //   selectedProduct: this.selectedProduct,
      //   selectedBatch: this.selectedBatch,
      //   selectedSupplier: this.selectedSupplier,
      //   selectedSerialNumber: this.selectedSerialNumber,
      //   selectedCell: this.selectedCell
      // });
      this.updateAllOptions();
    },
    
    // Формирование адреса ячейки
    getCellAddress(storehouseProduct) {
      const cell = storehouseProduct.storehouse_cell || storehouseProduct.storehouseCell;
      if (!cell) return '';

      // Новая архитектура: structureItem.full_path
      const structureItem = cell.structure_item || cell.structureItem;
      if (structureItem?.full_path) return structureItem.full_path;

      // Фолбэк: номер ячейки
      return cell.number || '';
    },
    
    // Блокировка скролла фона
    lockBodyScroll() {
      if (typeof document === 'undefined') return
      document.documentElement.style.overflow = 'hidden'
      document.body.style.overflow = 'hidden'
    },
    
    // Разблокировка скролла фона
    unlockBodyScroll() {
      if (typeof document === 'undefined') return
      document.documentElement.style.overflow = ''
      document.body.style.overflow = ''
    },

    // Сохранение
    async saveSelection() {
      // Проверка что все селекты заполнены
      if (!this.canSave) {
        alert('Выберите продукцию и серийный номер');
        return;
      }
      
      // Находим конкретный StorehouseProduct
      // console.log('Поиск продукта по фильтрам:', {
      //   selectedProduct: this.selectedProduct,
      //   selectedBatch: this.selectedBatch,
      //   selectedSupplier: this.selectedSupplier,
      //   selectedSerialNumber: this.selectedSerialNumber,
      //   selectedCell: this.selectedCell
      // });
      
      // console.log('Доступные продукты для поиска:', this.allStorehouseProducts.map(p => ({
      //   product_id: p.product_id,
      //   batch_number: p.batch_product?.batch_manufacture?.number,
      //   supplier_name: p.batch_product?.batch_manufacture?.supplier?.name,
      //   serial_number: p.batch_product?.serial_number,
      //   cell_address: this.getCellAddress(p)
      // })));
      
      const targetProduct = this.allStorehouseProducts.find(p => {
        // Обязательные проверки
        if (p.product_id !== this.selectedProduct) return false;
        if (p.batch_product?.serial_number !== this.selectedSerialNumber) return false;

        // Опциональные фильтры (только если выбраны)
        if (this.selectedCell && this.getCellAddress(p) !== this.selectedCell) return false;
        if (this.selectedBatch && p.batch_product?.batch_manufacture?.number !== this.selectedBatch) return false;
        if (this.selectedSupplier && p.batch_product?.batch_manufacture?.supplier?.name !== this.selectedSupplier) return false;

        return true;
      });
      
      if (!targetProduct) {
        alert('Продукт с выбранным серийным номером не найден на складе');
        return;
      }
      
      // Отправляем запрос на создание запланированного компонента
      try {
        await axios.post('/api/component-planning/create-planned', {
          production_task_id: this.productionTaskId,
          operation_step_id: this.operationStepId,
          storehouse_product_id: targetProduct.id
        });
        
        // Обновляем данные заказа после сохранения
        this.$emit('saved');
        this.closeModal();
        // Перезагружаем заказ
        if (this.$parent.fetchOrder) {
          this.$parent.fetchOrder(this.$route.params.id);
        }
      } catch (error) {
        alert('Ошибка сохранения: ' + (error.response?.data?.error || error.message));
      }
    }
  }
}
</script>

<style>
@import "vue-select/dist/vue-select.css";
</style>
